import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import {  Search } from 'lucide-react';
import debounce from 'lodash/debounce';
import Pagination from '../components/Paginate';
import LoanTable from '../Table/LoanTable';
import DateRangePicker from '../components/DateRangePicker';
import { fetchLoanApplication, resetFilters, setFilter } from '../store/slices/loanApplicationSlice';

const PendingLoans = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { loanApplications, pagination, loading, filters } = useSelector((state) => state.loanApplications);
  const [localSearch, setLocalSearch] = useState(filters.search);
  const [isInitialized, setIsInitialized] = useState(false);
  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        dispatch(setFilter({ search: value, page: 1 }));
      }, 500),
    [dispatch]
  ); 

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

 
  useEffect(() => {
    const restoreFilters = () => {
      console.log(searchParams.get("userid"))
      const initialFilters = {
        page: parseInt(searchParams.get("page")) || 1,
        limit: parseInt(searchParams.get("limit")) || 10,
        search: searchParams.get("search") || "",
        sortBy: searchParams.get("sortBy") || "createdAt",
        type:searchParams.get("type") || "loan_ready_to_payout",
        userId: searchParams.get("userId") || "",

        sortOrder: searchParams.get("sortOrder") || "desc",
        startDate: searchParams.get("startDate") ? new Date(searchParams.get("startDate")) : null,
        endDate: searchParams.get("endDate") ? new Date(searchParams.get("endDate")) : null
      };
      const hasUrlParams = Array.from(searchParams.entries()).length > 0;
      console.log(hasUrlParams)
      if (hasUrlParams) {
        dispatch(setFilter(initialFilters));
        setLocalSearch(initialFilters.search);
      } else {
        const defaultFilters = { 
          page: 1, 
          limit: 10, 
          search: "",
          userId:"",
          type:"loan_ready_to_payout",
          sortBy: "createdAt",
          sortOrder: "desc"
        };
        console.log(defaultFilters)
        dispatch(setFilter(defaultFilters));
        setSearchParams(defaultFilters, { replace: true });
      }
      setIsInitialized(true);
    };

    restoreFilters();
  }, []);

  const statusOptions = {
    PENDING: "pending",
    PERSONAL_INFO_COMPLETED: "personal_info_completed",
    LOAN_OFFER: "loan_offer",
    LOAN_READY_PAYOUT: "loan_ready_to_payout",
    LOAN_CREATED: "loan_created",
    LOAN_DISBURSED: "loan_disbursed",
  };
  useEffect(() => {
    if (!isInitialized) return;

    const updateUrlAndFetch = () => {
      const params = new URLSearchParams();
      const isIncompleteDateRange = 
      (filters.startDate && !filters.endDate) || 
      (!filters.startDate && filters.endDate);

    if (isIncompleteDateRange) {
      return; 
    }
      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          if (key === 'page' && value === 1) return;
          if (key === 'limit' && value === 10) return;
          if (key === 'search' && value === '') return;
          if (key === 'sortBy' && value === 'createdAt') return;
          if (key === 'sortOrder' && value === 'desc') return;
          params.set(key, value instanceof Date ? value.toISOString().split('T')[0] : value);
         
        }
      });

      const currentParams = new URLSearchParams(searchParams);
     
      const paramsChanged = Array.from(params.entries()).some(
        ([key, value]) => currentParams.get(key) !== value
      ) || 
      (currentParams.has('startDate') && !filters.startDate) ||
      (currentParams.has('endDate') && !filters.endDate);
      if (filters.userId) {
        params.set('userId', filters.userId);
      }
      if (paramsChanged) {
        setSearchParams(params, { replace: true });
      }

      dispatch(fetchLoanApplication());
    };

    updateUrlAndFetch();
  }, [filters, isInitialized]);

  const handleSearchChange = useCallback(
    (e) => {
      const value = e.target.value;
      setLocalSearch(value);
      debouncedSearch(value);
    },
    [debouncedSearch]
  );



  const handleClearFilters = useCallback(() => {
    dispatch(resetFilters());
    setLocalSearch("");
    debouncedSearch.cancel();
    setSearchParams({}, { replace: true });
  }, [dispatch, debouncedSearch, setSearchParams]);

  const handleStatusChange = (e) => {
    dispatch(setFilter({ type: e.target.value, page: 1 }));
  };



  const handleDateChange = (dates) => {
    if (!dates.startDate && !dates.endDate) {
      dispatch(setFilter({ startDate: null, endDate: null, page: 1 }));
    } else {
      dispatch(setFilter({ ...dates, page: 1 }));
    }
  };


  return (
    <div className="p-6 space-y-6">
 
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold">Pending Loans</h1>
        <button
          onClick={handleClearFilters}
          className="px-4 py-2 border border-gray-200 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors"
        >
          Clear Filters
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search loan Applications..."
            value={localSearch}
            onChange={handleSearchChange}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
 
        <div className="w-full sm:w-[300px]">
        <DateRangePicker
          startDate={filters.startDate}
          endDate={filters.endDate}
          onChange={handleDateChange}
        />
     </div>
      </div>

  
      <div className="rounded-lg border border-gray-200 overflow-hidden">
      <LoanTable
          loans={loanApplications}
          loading={loading}
          filters={filters}
          isApplication={true}
         
        />
      </div>

     
      {!loading && loanApplications?.length > 0 && (
      <Pagination
        currentPage={filters.page}
    totalItems={pagination.totalItems}
    pageSize={filters.limit}
    onPageChange={(page) => dispatch(setFilter({ page }))}
    onPageSizeChange={(limit) => dispatch(setFilter({ limit, page: 1 }))}
        />
      )}
    </div>
  );
};

export default PendingLoans;
